import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import CircleIcon from '@mui/icons-material/Circle';
import { glassCardSx, landingTheme } from './landingTheme';

const STATIONS = [
  { name: 'Pump #1', status: 'ok', value: '42.3 bar' },
  { name: 'Tank A', status: 'warn', value: '78%' },
  { name: 'Line B', status: 'ok', value: 'ON' },
  { name: 'Boiler', status: 'ok', value: '92°C' },
];

function statusColor(status) {
  if (status === 'warn') return '#ffb74d';
  return '#66bb6a';
}

/** Decorative SCADA dashboard preview for the landing hero. */
export default function HeroMockup() {
  return (
    <Box
      sx={{
        position: 'relative',
        animation: 'landingFloat 6s ease-in-out infinite',
        '@keyframes landingFloat': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-10px)' },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: '-20%',
          background: `radial-gradient(circle, rgba(92,108,255,0.25) 0%, transparent 65%)`,
          filter: 'blur(40px)',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          ...glassCardSx,
          position: 'relative',
          zIndex: 1,
          p: 2,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${landingTheme.accent}, ${landingTheme.cyan}, ${landingTheme.violet})`,
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ color: landingTheme.textMuted, letterSpacing: 1 }}>
              LIVE OVERVIEW
            </Typography>
            <Typography variant="subtitle1" sx={{ color: landingTheme.text, fontWeight: 700 }}>
              demo-facility
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75}>
            <Chip
              size="small"
              icon={<CircleIcon sx={{ fontSize: '10px !important', color: '#66bb6a !important' }} />}
              label="3 online"
              sx={{
                height: 24,
                fontSize: 11,
                bgcolor: 'rgba(102, 187, 106, 0.12)',
                color: '#a5d6a7',
                border: '1px solid rgba(102, 187, 106, 0.25)',
              }}
            />
            <Chip
              size="small"
              label="Pro"
              sx={{
                height: 24,
                fontSize: 11,
                bgcolor: 'rgba(92, 108, 255, 0.15)',
                color: landingTheme.accentBright,
                border: '1px solid rgba(92, 108, 255, 0.3)',
              }}
            />
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 1.25,
            mb: 2,
          }}
        >
          {STATIONS.map((st) => (
            <Box
              key={st.name}
              sx={{
                p: 1.25,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                <CircleIcon sx={{ fontSize: 8, color: statusColor(st.status) }} />
                <Typography variant="caption" sx={{ color: landingTheme.textSecondary, fontWeight: 600 }}>
                  {st.name}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: landingTheme.text, fontWeight: 700, fontFamily: 'monospace' }}>
                {st.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            p: 1.25,
            borderRadius: 2,
            bgcolor: 'rgba(0, 188, 212, 0.06)',
            border: '1px solid rgba(0, 188, 212, 0.15)',
          }}
        >
          <Typography variant="caption" sx={{ color: landingTheme.cyan, fontWeight: 600 }}>
            Module store
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            {['Trends', 'Telegram', 'SVG HMI'].map((m) => (
              <Chip
                key={m}
                label={m}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 10,
                  bgcolor: 'rgba(255,255,255,0.05)',
                  color: landingTheme.textSecondary,
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              />
            ))}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
